/**
 * LiveTestingSxCollectAssociatedField
 *
 * sx_collect_associated 타입 질문을 위한 필드 컴포넌트
 * V1 QsInputSxAssociatedCollector의 V2 버전
 *
 * 기능:
 * - 이미 선택된 증상 트리들에서 연관 증상을 추출
 * - 연관 증상 목록을 체크박스로 표시
 * - 체크박스 클릭 시 해당 증상 트리의 질문들을 모달에서 표시
 * - "None of the above" 옵션 지원
 */

import { memo, useCallback, useMemo, useEffect, useRef } from 'react';
import { includes, isEmpty } from 'lodash';
import classNames from 'classnames';
import type { QuestionResponseDto } from '../../../../api/generated/types';
import type { V2QsSxCollectIntelligentValue } from '../../../../shared/components/question-types/V2QsSxCollectIntelligent';
import { useAssociatedSymptoms } from './useAssociatedSymptoms';
import { useLiveTestingSxSelect } from './useLiveTestingSxSelect';
import LiveTestingSxSelectModal from './LiveTestingSxSelectModal';
import LiveTestingContainer from './LiveTestingContainer';
import { useTranslations } from '../../../../../patient/hooks/use-translations';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import { getLocaleCode } from '../../../organization/configurations/language/constants';
import LoadingAnim from '../../../../../components/LoadingAnim';

// "None of the above" 키 상수
const NONE_ABOVE_KEY = 'o-none-above';


interface LiveTestingSxCollectAssociatedFieldProps {
  /** 질문 데이터 */
  question: QuestionResponseDto;
  /** 현재 답변 값 */
  value: V2QsSxCollectIntelligentValue;
  /** 값 변경 핸들러 */
  onChange: (value: V2QsSxCollectIntelligentValue) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 연관 증상이 없어서 스킵할 때 호출 */
  onSkip?: () => void;
}

function LiveTestingSxCollectAssociatedField({
  question: _question,
  value,
  onChange,
  disabled = false,
  onSkip,
}: LiveTestingSxCollectAssociatedFieldProps) {
  // Container에서 필요한 정보 가져오기
  const { surveyId, currentLocale, selectedSymptomTreeKeys, reportState, survey } =
    LiveTestingContainer.useContainer();

  const [, { t }] = useTranslations({ locale: currentLocale });
  const localeCode = getLocaleCode(currentLocale || 'english') as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode });

  // 연관 증상 키 추출
  const { associatedSymptomKeys, associatedSymptomOptions, isLoading, error } =
    useAssociatedSymptoms({
      selectedSymptomTreeKeys: selectedSymptomTreeKeys || [],
      surveyId: surveyId ? Number(surveyId) : undefined,
      locale: currentLocale,
      enabled: !!surveyId && (selectedSymptomTreeKeys?.length || 0) > 0,
      reportState,
      survey,
    });

  // 자동 스킵: 연관 증상이 없으면 "None of the above" 선택 후 onSkip 호출
  const hasAutoSkipped = useRef(false);
  useEffect(() => {
    // 이미 스킵했거나, 로딩 중이거나, 에러가 있으면 무시
    if (hasAutoSkipped.current || isLoading || error) return;
    // 연관 증상이 없고, 아직 답변이 없는 경우에만 자동 스킵
    if (isEmpty(associatedSymptomKeys) && isEmpty(value?.selectedKeys)) {
      hasAutoSkipped.current = true;
      // "None of the above" 자동 선택
      onChange({ selectedKeys: [NONE_ABOVE_KEY], itemAnswers: {} } as any);
      // onSkip 호출하여 다음 단계로 이동
      if (onSkip) {
        // 약간의 지연을 두어 상태 업데이트가 반영되도록 함
        setTimeout(() => {
          onSkip();
        }, 100);
      }
    }
  }, [isLoading, error, associatedSymptomKeys, value?.selectedKeys, onChange, onSkip]);

  // 현재 선택된 키들
  const selectedKeys = value?.selectedKeys || [];

  // None of the above 선택 여부
  const noneAboveSelected = includes(selectedKeys, NONE_ABOVE_KEY);
  const visibleSelectedCount = selectedKeys.filter(
    (key) => key !== NONE_ABOVE_KEY && includes(associatedSymptomKeys, key)
  ).length;
  const disableNoneAbove = visibleSelectedCount > 0;

  // itemAnswers를 value에서 가져오기 (화면 이동 후에도 데이터 유지)
  const itemAnswers = (value as any)?.itemAnswers || {};

  // 숨김/미노출된 연관증상 키는 현재 질문 답변에서 정리
  const lastPruneRequestRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || error) return;

    const allowedKeys = new Set<string>([NONE_ABOVE_KEY, ...associatedSymptomKeys]);
    const nextSelectedKeys = selectedKeys.filter((key) => allowedKeys.has(key));
    const nextItemAnswers = Object.fromEntries(
      Object.entries(itemAnswers).filter(([key]) => nextSelectedKeys.includes(key))
    );

    const selectedChanged =
      nextSelectedKeys.length !== selectedKeys.length ||
      nextSelectedKeys.some((key, index) => key !== selectedKeys[index]);
    const currentItemAnswerKeys = Object.keys(itemAnswers).sort();
    const nextItemAnswerKeys = Object.keys(nextItemAnswers).sort();
    const itemAnswersChanged =
      nextItemAnswerKeys.length !== currentItemAnswerKeys.length ||
      nextItemAnswerKeys.some((key, index) => key !== currentItemAnswerKeys[index]);

    if (!(selectedChanged || itemAnswersChanged)) {
      if (lastPruneRequestRef.current) {
        lastPruneRequestRef.current = null;
      }
      return;
    }

    const nextSignature = `${nextSelectedKeys.join('|')}::${nextItemAnswerKeys.join('|')}`;
    if (lastPruneRequestRef.current === nextSignature) {
      return;
    }
    lastPruneRequestRef.current = nextSignature;
    onChange({ selectedKeys: nextSelectedKeys, itemAnswers: nextItemAnswers } as any);
  }, [associatedSymptomKeys, error, isLoading, itemAnswers, onChange, selectedKeys]);

  // sx_select hook 사용 (모달 처리용)
  const {
    modalState,
    handleOptionClick,
    handleModalClose,
    handleModalSave,
    updateModalAnswer,
    handleDelete,
  } = useLiveTestingSxSelect({
    answerKeys: associatedSymptomKeys,
    answerOptionsMap: Object.fromEntries(
      Object.entries(associatedSymptomOptions).map(([key, val]) => [
        key,
        { label: val.label, dataType: val.dataType },
      ])
    ),
    value: { selectedKeys, notesForKeys: {}, itemAnswers } as any,
    onChange: (newValue) => {
      // itemAnswers도 함께 저장하여 화면 이동 후에도 유지되도록 함
      onChange({ selectedKeys: newValue.selectedKeys, itemAnswers: (newValue as any)?.itemAnswers || itemAnswers } as any);
    },
    locale: currentLocale,
    surveyId: surveyId ? Number(surveyId) : undefined,
    suppressSxCollectors: true,
  });

  // None of the above 체크 핸들러
  const handleNoneAboveChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        // None above 선택 시 다른 모든 선택 해제
        onChange({ selectedKeys: [NONE_ABOVE_KEY], itemAnswers: {} } as any);
      } else {
        // None above 해제
        onChange({ selectedKeys: selectedKeys.filter((k) => k !== NONE_ABOVE_KEY), itemAnswers } as any);
      }
    },
    [selectedKeys, onChange, itemAnswers]
  );

  // 증상 클릭 핸들러 - 모달만 열고, Save 시에만 체크됨
  const handleSymptomClick = useCallback(
    (sxKey: string, isCurrentlySelected: boolean) => {
      if (isCurrentlySelected) {
        // 이미 선택된 경우: 체크 해제
        const newItemAnswers = { ...itemAnswers };
        delete newItemAnswers[sxKey];
        onChange({ selectedKeys: selectedKeys.filter((k) => k !== sxKey), itemAnswers: newItemAnswers } as any);
      } else {
        // 미선택인 경우: 모달만 열기 (Save 시 실제 체크됨)
        handleOptionClick(sxKey);
      }
    },
    [selectedKeys, onChange, handleOptionClick, itemAnswers]
  );

  // 모달 저장 핸들러 - 저장 시 체크도 함께 처리
  const handleSaveAndCheck = useCallback(() => {
    const sxKey = modalState.selectedSxKey;
    if (sxKey && !includes(selectedKeys, sxKey)) {
      // None above 제거 후 추가
      const newKeys = [...selectedKeys.filter((k) => k !== NONE_ABOVE_KEY), sxKey];
      onChange({ selectedKeys: newKeys, itemAnswers } as any);
    }
    handleModalSave();
  }, [modalState.selectedSxKey, selectedKeys, onChange, handleModalSave, itemAnswers]);

  // 모달 삭제 핸들러
  const handleDeleteSymptom = useCallback(() => {
    const sxKey = modalState.selectedSxKey;
    if (sxKey) {
      handleDelete(sxKey);
      handleModalClose();
    }
  }, [modalState.selectedSxKey, handleDelete, handleModalClose]);

  // 선택된 증상 이름
  const selectedSymptomName = useMemo(() => {
    if (!modalState.selectedSxKey) return '';
    const option = associatedSymptomOptions[modalState.selectedSxKey];
    return option?.label || t(modalState.selectedSxKey) || modalState.selectedSxKey;
  }, [modalState.selectedSxKey, associatedSymptomOptions, t]);

  // 스타일 클래스
  const getOptionClasses = (isSelected: boolean, isDisabled: boolean) =>
    classNames(
      'flex flex-col gap-3 transition-all duration-200',
      'border-[1.5px] p-3 rounded-[10px]',
      {
        'border-[#4B7BF5]': isSelected && !isDisabled,
        'border-[#E6E9F5]': !isSelected && !isDisabled,
        'border-[#D0D4E4]': isDisabled,
        'bg-white': !isDisabled,
        'bg-[#F8F9FC]': isDisabled,
        'hover:border-[#DADDEB]': !isSelected && !isDisabled,
        'cursor-default': isDisabled,
        'cursor-pointer': !isDisabled,
      }
    );

  const inputClasses = (isDisabled: boolean) =>
    classNames('checkbox checkbox-lg checkbox-primary shrink-0', {
      '!border-[2.5px] !border-[#9CA3B8]': isDisabled,
    });

  // 선택된 증상을 상단으로 정렬하고 전체 항목을 표시 (hooks must be called before early returns)
  const sortedSymptomKeys = useMemo(() => {
    const selected = associatedSymptomKeys.filter((key) => includes(selectedKeys, key));
    const unselected = associatedSymptomKeys.filter((key) => !includes(selectedKeys, key));
    return [...selected, ...unselected];
  }, [associatedSymptomKeys, selectedKeys]);

  // 영역 클릭 핸들러
  const handleAreaClick = useCallback(
    (sxKey: string, isSelected: boolean) => {
      if (disabled) return;
      handleSymptomClick(sxKey, isSelected);
    },
    [disabled, handleSymptomClick]
  );

  // 로딩 중
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingAnim width={28} />
      </div>
    );
  }

  // 에러
  if (error) {
    return (
      <div className="text-red-500 text-sm p-4">{error}</div>
    );
  }

  // 연관 증상이 없는 경우
  if (isEmpty(associatedSymptomKeys)) {
    return (
      <div className="text-gray-500 text-sm p-4">
        No associated symptoms found. Please select symptoms first.
      </div>
    );
  }

  return (
    <div className="t_LiveTestingSxCollectAssociatedField flex flex-col gap-3">
      {/* 연관 증상 목록 */}
      {sortedSymptomKeys.map((sxKey) => {
        const isSelected = includes(selectedKeys, sxKey);
        const option = associatedSymptomOptions[sxKey];
        const label = option?.label || t(sxKey) || sxKey;

        return (
          <div
            key={sxKey}
            className={getOptionClasses(isSelected, disabled)}
            onClick={() => handleAreaClick(sxKey, isSelected)}
          >
            <div className="flex-1 w-full flex flex-row gap-4 items-start">
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                className={inputClasses(disabled)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSymptomClick(sxKey, isSelected);
                }}
              />
              <span className="pt-[0.2rem] text-[15px] text-[#222638]">
                {label}
              </span>
            </div>
          </div>
        );
      })}

      {/* None of the above 옵션 */}
      <div
        className={classNames(
          'flex flex-col gap-3 transition-all duration-200',
          'border-[1.5px] p-3 rounded-[10px]',
          {
            'border-[#4B7BF5]': noneAboveSelected,
            'border-[#E6E9F5]': !noneAboveSelected && !disableNoneAbove,
            'border-[#D0D4E4]': disableNoneAbove,
            'opacity-50': disableNoneAbove,
            'cursor-pointer': !disabled && !disableNoneAbove,
            'cursor-default': disabled || disableNoneAbove,
          }
        )}
        onClick={() => {
          if (disabled || disableNoneAbove) return;
          handleNoneAboveChange(!noneAboveSelected);
        }}
      >
        <div className="flex-1 w-full flex flex-row gap-4 items-start">
          <input
            type="checkbox"
            checked={noneAboveSelected}
            disabled={disabled || disableNoneAbove}
            className={inputClasses(disableNoneAbove)}
            onChange={(e) => {
              e.stopPropagation();
              handleNoneAboveChange(e.target.checked);
            }}
          />
          <span className="pt-[0.2rem] text-[15px] text-[#222638]">
            {t(NONE_ABOVE_KEY) || 'None of the above'}
          </span>
        </div>
      </div>

      {/* 증상 상세 모달 */}
      <LiveTestingSxSelectModal
        isOpen={modalState.isOpen}
        symptomName={selectedSymptomName}
        questions={modalState.questions}
        answers={modalState.answers}
        onAnswerChange={updateModalAnswer}
        onSave={handleSaveAndCheck}
        onClose={handleModalClose}
        onDelete={modalState.selectedSxKey && includes(selectedKeys, modalState.selectedSxKey) ? handleDeleteSymptom : undefined}
        isLoading={modalState.isLoading}
        error={modalState.error}
        saveButtonText={sltStr({ key: 'btn-save', capit: true })}
        deleteButtonText={sltStr({ key: 'btn-delete-symptom', title: true })}
      />
    </div>
  );
}

export default memo(LiveTestingSxCollectAssociatedField);
